import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "outline" | "ghost" | "danger" | "destructive";
type Size = "sm" | "md" | "lg" | "icon";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const VARIANT: Record<Variant, string> = {
  primary:
    "bg-magenta text-white hover:bg-magenta-hover active:bg-magenta-active rounded-lg",
  secondary:
    "bg-surface-2 text-text-primary border border-border hover:bg-surface-3 hover:border-border-strong rounded-lg",
  outline:
    "bg-transparent text-text-primary border border-border hover:bg-surface-2 hover:border-border-strong rounded-lg",
  ghost:
    "bg-transparent text-text-secondary hover:bg-surface-2 hover:text-text-primary rounded-lg",
  danger: "bg-error text-white hover:bg-error/90 rounded-lg",
  // Alias legacy — equivalente a `danger` para evitar breaking changes.
  destructive: "bg-error text-white hover:bg-error/90 rounded-lg",
};

const SIZE: Record<Size, string> = {
  sm: "h-8 px-3 text-sm",
  md: "h-9 px-4 text-base",
  lg: "h-10 px-5 text-md",
  icon: "h-9 w-9 rounded-md p-0",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant = "primary", size = "md", loading, disabled, children, ...props },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={cn(
          "inline-flex items-center justify-center gap-2 font-sans font-medium",
          "transition-colors duration-150",
          "focus:outline-none focus-visible:shadow-focus",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          VARIANT[variant],
          SIZE[size],
          className,
        )}
        {...props}
      >
        {loading ? (
          <span
            className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-r-transparent"
            aria-hidden="true"
          />
        ) : null}
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";
